import { ThemeOptions } from '@mui/material'
import { TEMPLE, TEMPLE_RADIUS, TEMPLE_SHADOW } from '../components/common/templeStyles'

const themeConfig = (): ThemeOptions => {
    const mode = 'light'
    const config = {
        typography: {
            htmlFontSize: 16,
            fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
            fontSize: 14,
            fontWeightLight: 300,
            fontWeightRegular: 400,
            fontWeightMedium: 500,
            fontWeightBold: 700,
            h1: { fontSize: '1.75rem', fontWeight: '500', lineHeight: 1.4 },
            h2: { fontSize: '1.60rem', fontWeight: '500', lineHeight: 1.4 },
            h3: { fontSize: '1.45rem', fontWeight: '500', lineHeight: 1.4 },
            h4: { fontSize: '1.30rem', fontWeight: '500', lineHeight: 1.4 },
            h5: { fontSize: '1.15rem', fontWeight: 400, lineHeight: 1.4 },
            h6: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.4 },
            subtitle1: { fontSize: '0.875rem', fontWeight: 'normal', lineHeight: 1.4 },
            subtitle2: { fontSize: '0.875rem', fontWeight: 'normal', lineHeight: 1.4 },
            body1: { fontSize: '0.875rem', fontWeight: 'normal', lineHeight: 1.43 },
            body2: { fontSize: '0.875rem', fontWeight: 'normal', lineHeight: 1.4 },
            button: { fontSize: '0.875rem', fontWeight: 'normal', textTransform: 'uppercase' },
            caption: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.4 },
            overline: { fontSize: '0.75rem', fontWeight: '500', textTransform: 'uppercase', lineHeight: 1.1 },
            inherit: { fontSize: 'inherit', fontWeight: 'inherit', textTransform: 'inherit' }
        },
        shape: { borderRadius: 5 },
        zIndex: { mobileStepper: 1000, fab: 1050, speedDial: 1050, appBar: 1100, drawer: 1200, modal: 1300, snackbar: 1400, tooltip: 1500 },
        overrides: {
            MuiCssBaseline: {
                '@global': {
                    'a': {
                        color: TEMPLE.saffronD,
                        textDecoration: 'none',
                        '&:hover': {
                            textDecoration: 'underline',
                        },
                    },
                },
            },
        },
        components: {
            MuiLink: {
                styleOverrides: {
                    root: {
                        textDecoration: 'none',
                        color: TEMPLE.saffronD,
                        '&:hover': { textDecoration: 'underline' },
                        '&:visited': { color: TEMPLE.saffronD }
                    }
                }
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        fontWeight: 500,
                        borderRadius: '16px', 
                    },
                    startIcon: {
                        marginRight: '3px',
                    },
                    endIcon: {
                        marginLeft: '3px',
                    }
                },
                variants: [
                    {
                        props: { size: 'small' },
                        style: { fontSize: '0.8rem', lineHeight: 1.1, padding: '4px 15px' }
                    },
                    {
                        props: { size: 'medium' },
                        style: { fontSize: '0.8rem', lineHeight: 1.5, padding: '5px 17px' }
                    },
                    {
                        props: { size: 'large' },
                        style: { fontSize: '1.063rem', lineHeight: 1.44, padding: '10px 15px' }
                    }
                ],
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: {
                        backgroundColor: TEMPLE.cream2,
                        borderTop: `1px solid ${TEMPLE.line}`,
                        padding: '8px 24px'
                    }
                }
            },
            MuiTextField: {
                styleOverrides: {
                  root: {
                    size: 'small', // you can keep this if you're using <TextField size="small" />
              
                    '& .MuiInputBase-root': {
                      padding: 0,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '13px',
                      padding: '9px 12px',
                    },
                    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: TEMPLE.saffronL,    // ← light saffron on focus
                      borderWidth: '2px',
                    },
                    '& .Mui-focused:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: TEMPLE.saffron,     // slightly deeper on focus+hover
                    },
                  },
                },
              },
            MuiSelect: {
                styleOverrides: {
                    root: {
                        '& .MuiSelect-select': { fontSize: '13px', padding: '7px 12px' }
                    },
                }
            },
            MuiFormHelperText: {
                styleOverrides: {
                    root: {
                        fontSize: '0.7rem',
                        fontWeight: 400
                    }
                }
            },
            /* ---- Temple theme: tables, pagination and paper surfaces ----
               Applied at the theme level so every admin table inherits the
               cream/saffron look without a per-page opt-in. Guest routes use
               Tailwind and render no MUI Table/Pagination, so they are
               unaffected. Tokens mirror `components/common/templeStyles`. */
            MuiTableContainer: {
                styleOverrides: {
                    root: {
                        backgroundColor: '#fff',
                        border: `1px solid ${TEMPLE.line}`,
                        borderRadius: `${TEMPLE_RADIUS}px`,
                        boxShadow: TEMPLE_SHADOW,
                    }
                }
            },
            MuiTableCell: {
                styleOverrides: {
                    /* Vertical column rules on every table; the trailing cell of a
                       row drops its rule so it never doubles the container border. */
                    root: {
                        borderBottom: `1px solid ${TEMPLE.line}`,
                        borderRight: `1px solid ${TEMPLE.line}`,
                        '&:last-of-type': { borderRight: 'none' },
                    },
                    head: {
                        backgroundColor: TEMPLE.cream2,
                        color: TEMPLE.inkM,
                        fontSize: '12px',
                        fontWeight: 700,
                        letterSpacing: '0.2px',
                        textTransform: 'capitalize',
                        borderBottom: `1px solid ${TEMPLE.lineD}`,
                        borderRight: `1px solid ${TEMPLE.lineD}`,
                        whiteSpace: 'nowrap',
                    },
                    body: {
                        color: TEMPLE.inkM,
                        fontSize: '13.5px',
                    }
                }
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        '&.MuiTableRow-hover:hover': {
                            backgroundColor: TEMPLE.cream,
                        }
                    }
                }
            },
            MuiPaginationItem: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: TEMPLE.inkM,
                        borderColor: TEMPLE.line,
                        '&:hover': { backgroundColor: TEMPLE.cream2 },
                        '&.Mui-selected': {
                            background: `linear-gradient(135deg, ${TEMPLE.saffron}, ${TEMPLE.saffronD})`,
                            borderColor: 'transparent',
                            color: '#fff',
                            '&:hover': {
                                background: `linear-gradient(135deg, ${TEMPLE.saffron}, ${TEMPLE.saffronD})`,
                            }
                        }
                    },
                    ellipsis: { color: TEMPLE.inkL }
                }
            }
        }
    }

    const palette: any = {
        light: {
            /* Temple palette: saffron primary, maroon secondary, gold accents on a
               cream canvas. Mirrors `components/common/templeStyles`, which stays the
               single source of truth for the raw hex values. */
            palette: {
                mode: 'light',
                common: { black: '#000', white: '#fff' },
                primary: { main: TEMPLE.saffron, light: TEMPLE.saffronL, light2: TEMPLE.cream3, dark: TEMPLE.saffronD, disabled: '#F0B98A', contrastText: '#fff' },
                secondary: { main: TEMPLE.maroon, light: '#A34747', light2: TEMPLE.cream3, dark: TEMPLE.maroonD, disabled: '#B98484', contrastText: '#fff' },
                error: { main: '#C1352A', light: '#D9584C', light2: '#FBE7E4', dark: '#8E2118', disabled: '#E0968E', contrastText: '#fff' },
                warning: { main: TEMPLE.goldD, light: TEMPLE.gold, light2: '#FDF1DA', dark: '#7E5D14', disabled: '#DCC489', contrastText: '#fff' },
                info: { main: '#3A6B8A', light: '#5B8AA8', light2: '#E4EEF5', dark: '#2A5570', disabled: '#9DBACC', contrastText: '#fff' },
                custom: { main: TEMPLE.gold, light: TEMPLE.goldL, light2: TEMPLE.cream2, dark: TEMPLE.goldD, disabled: '#DCC489', contrastText: '#fff' },
                success: { main: '#2e7d32', light: '#4caf50', light2: '#E7F4EA', dark: '#1b5e20', disabled: '#a8cb7f', contrastText: '#fff' },
                grey: { 50: '#fafafa', 100: '#f5f5f5', 200: '#eeeeee', 300: '#e0e0e0', 400: '#bdbdbd', 500: '#9e9e9e', 600: '#757575', 700: '#616161', 800: '#424242', 900: '#212121', A100: '#f5f5f5', A200: '#eeeeee', A400: '#bdbdbd', A700: '#616161' },
                contrastThreshold: 3,
                tonalOffset: 0.2,
                text: { main: TEMPLE.ink, primary: TEMPLE.ink, secondary: TEMPLE.inkM, warning: TEMPLE.goldD, success: '#2e7d32', error: '#C1352A', disabled: TEMPLE.inkL },
                divider: TEMPLE.line,
                background: { paper: '#fff', default: TEMPLE.cream },
                action: { active: TEMPLE.inkM, hover: 'rgba(232, 114, 28, 0.06)', hoverOpacity: 0.06, selected: 'rgba(232, 114, 28, 0.12)', selectedOpacity: 0.12, disabled: TEMPLE.inkL, disabledBackground: TEMPLE.cream3, disabledOpacity: 0.38, focus: 'rgba(232, 114, 28, 0.12)', focusOpacity: 0.12, activatedOpacity: 0.12 }
            },
            shadows: [
                'none',
                '3px 0px 3px 1px rgba(0,0,0,0.07)', // top
                '3px -3px 3px 1px rgba(0,0,0,0.07)', // right
                '3px 0px 3px 1px rgba(0,0,0,0.07)', // bottom
                '3px 0px 3px 1px rgba(0,0,0,0.07)', // left
                '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
                '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
                '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
                '0px 2px 4px -1px rgba(0,0,0,0.4),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
                '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
                '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
                '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
                '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
                '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
                '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
                '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
                '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
                '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
                '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
                '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
                '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
                '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
                '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
                '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
                '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
                '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
                '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
                '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
                '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)'
            ],
        }
    }

    return { ...palette[mode], ...config }
}

export default themeConfig
